import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/config/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, impersonateUser, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [quickUsers, setQuickUsers] = useState([]);
  const [titleTaps, setTitleTaps] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users`);
        if (Array.isArray(response.data)) {
          // Filter for specific users if needed, or just take first 4 distinct roles/people
          // For now, take all to match UserSelection logic but limit to 4 for UI
          setQuickUsers(response.data.slice(0, 4));
        }
      } catch (e) { console.error(e); }
    };
    fetchUsers();
  }, []);

  // Automatic redirect if already logged in
  React.useEffect(() => {
    if (!authLoading && user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'manager') {
        navigate('/manager');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const result = await login(email);

    if (result.success) {
      setMessage(result.message);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const quickLogin = async (userData) => {
    setLoading(true);
    impersonateUser(userData);
    if (userData.role === 'admin') {
      navigate('/admin');
    } else if (userData.role === 'manager') {
      navigate('/manager');
    } else {
      navigate('/dashboard');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen mafia-gradient flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <h1
            className="font-display text-5xl font-bold gold-text mb-2 select-none cursor-default"
            onClick={() => setTitleTaps(t => t + 1)}
          >Don Doit</h1>
          <p className="text-muted-foreground text-lg">Il Timesheet della Famiglia 🤌🇫🇷</p>
          <p className="text-muted-foreground/60 text-xs mt-1 italic">"Don't do it? No no, DO IT!"</p>
        </div>

        <Card className="card-mafia animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl gold-text">Entra nella Famiglia</CardTitle>
            <CardDescription>Solo i membri autorizzati possono accedere...</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="La tua email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-mafia h-12"
                  data-testid="email-input"
                />
              </div>

              {error && (
                <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {message && (
                <Alert className="bg-green-500/10 border-green-500/30 text-green-500">
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full btn-gold h-12 text-lg font-semibold"
                disabled={loading || !email}
                data-testid="login-button"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verificando...</>
                ) : (
                  'Accedi'
                )}
              </Button>
            </form>

            {/* Quick login buttons — hidden until title is tapped 4 times */}
            {titleTaps >= 4 && <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground text-center mb-4">Accesso rapido:</p>
              <div className="grid grid-cols-2 gap-2">
                {quickUsers.length > 0 ? quickUsers.map(u => (
                  <Button
                    key={u.id}
                    variant="outline"
                    size="sm"
                    onClick={() => quickLogin(u)}
                    disabled={loading}
                    className="text-xs card-mafia text-white hover:bg-gold/20 min-h-[44px]"
                  >
                    {u.name}
                  </Button>
                )) : (
                  <p className="col-span-2 text-xs text-center text-zinc-500">Caricamento utenti...</p>
                )}
              </div>
            </div>}

            {/* Signup link */}
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">
                Non sei ancora un membro?{' '}
                <Link to="/signup" className="text-primary hover:underline font-semibold">
                  Unisciti alla Famiglia
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-muted-foreground text-xs mt-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          La Famiglia QuiteBold © 2025
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
